// What a session's tokens are WORTH, as a thing a component can render.
//
// The rule this whole module serves: a number shown to a customer must be
// traceable to a source AND labelled as measurement / estimate / reference. This
// one is a REFERENCE — public OpenRouter list rates applied to the tokens the run
// actually reported — and it is never a bill. developerz.ai is BYOK: the customer
// brings their own key, we never resell tokens, and a large share of prod runs
// are paid for by a flat-rate subscription whose marginal cost is genuinely $0.
// So the figure exists to give sessions a magnitude comparable under the same
// card, and the copy beside it has to make "worth" and "charged" impossible to
// confuse.

import type { SessionDetail } from './sessionDetail';

/**
 * Why a session's worth is absent, mapped to the i18n key that says so.
 *
 * A lookup rather than one blank, because the three silences are different facts
 * and only one of them is anybody's to act on: `unlisted` means the model has no
 * OpenRouter listing (someone can add a rate row), `not_recorded` means the run
 * reported no tokens, and `legacy_unattributed` means the row predates the
 * column. Collapsing them is exactly the reading `reference_basis` exists to
 * prevent — it is the same defect `cost_usd` had before #2485, one column over.
 *
 * An unrecognized basis falls through to `unknown` rather than to a figure: a
 * value this build does not know is not one it may caption.
 */
export const WORTH_ABSENT_REASON: Readonly<Record<string, string>> = {
  unlisted: 'session_worth.unlisted',
  not_recorded: 'session_worth.not_recorded',
  legacy_unattributed: 'session_worth.legacy',
};

/** The renderable worth of a session, or the NAMED reason there is none. */
export type SessionWorth =
  | { kind: 'listed'; amount: string; tokens: number }
  | { kind: 'absent'; reasonKey: string; tokens: number };

/**
 * The smallest magnitude worth spelling out. Below it the figure is rendered as a
 * BOUND rather than a rounded number.
 */
const WORTH_FLOOR_USD = 0.0001;

/**
 * Format the reference figure.
 *
 * Three bands, and each exists to stop the same misreading. Deliberately NOT
 * `Intl.NumberFormat(style: 'currency')` at its two-decimal default: a real run
 * can be worth $0.0034, and rounding that to `$0.00` puts a confident zero over
 * work that happened — the precise reading this whole feature was built to
 * remove. Sub-cent figures keep four decimals, and anything under the fourth
 * decimal renders as `<$0.0001` rather than `$0.0000`, which would be that same
 * zero wearing two extra digits. The stored column floors a real magnitude at
 * $0.000001 (`MIN_REFERENCE_USD`), so this band is reachable.
 */
export function formatWorthUsd(usd: number): string {
  if (usd >= 0.01) return `$${usd.toFixed(2)}`;
  if (usd >= WORTH_FLOOR_USD) return `$${usd.toFixed(4)}`;
  return `<$${WORTH_FLOOR_USD.toFixed(4)}`;
}

/**
 * Resolve a session detail to what the header should render.
 *
 * `null` while the detail read is in flight or failed — the header degrades to
 * its generic form there, the same way it already does for repo/trigger/model.
 * Both arms carry the token count: the worth is `tokens x a published rate`, and
 * showing the multiplicand is what makes the figure checkable rather than
 * asserted.
 */
export function sessionWorth(d: SessionDetail | undefined): SessionWorth | null {
  if (!d) return null;
  const tokens = d.token_in + d.token_out;
  if (d.reference_basis === 'listed' && d.reference_usd !== null) {
    return { kind: 'listed', amount: formatWorthUsd(d.reference_usd), tokens };
  }
  return {
    kind: 'absent',
    reasonKey: WORTH_ABSENT_REASON[d.reference_basis] ?? 'session_worth.unknown',
    tokens,
  };
}
